/* ============================================================
   神戸リボン 営業日カレンダー
   ============================================================
   - 曜日ごとの営業時間は section の data-hours（テーマ設定 kr_opening_hours から）。
     これだけで営業日・定休日を描く（休業日を読み込めなくても空にしない）
   - section の data-ics-url があれば、その ICS（お店の Google カレンダーを中継したもの。
     workers/closures-ics）から休業日・短縮営業の日を読み込んで重ねる。読み込めたら ICS を正とする
     （定休日も ICS の繰り返し予定に入っていて、臨時に開ける日は EXDATE で抜いてあるため）
   - 読み込めたときだけ、HERO の [data-kr-today] に本日の営業状況を出す。
     読み込めないときは出さない（臨時休業の日に「営業中」と出さないため）
   - 日付と時刻は店のある日本時間で判定する
   - 毎週の決まりでない日（ICS の 1 回きりの予定：臨時の休業・短縮営業）のマスに is-special を付け、
     短縮営業か is-special の日を表の下の一覧（ul.cal-notes）に書き出す。スマホではマスに札の文字が入らないので、
     この一覧で内容を読ませる（形はデザインシステム v2.0.0 の Calendar。パソコンでは一覧を隠す）
   - 元実装: design/mockups/2026-05-08-calendar/calendar.js
   - 依存: ical.min.js v1.5.0（theme assets に同梱、MPL 2.0、A-13）。ICS URL があるときだけ読まれる
============================================================ */
(function () {
  'use strict';

  /* ============================================================
     設定
     ============================================================ */
  // 0=日 .. 6=土。null は定休日。data-hours が読めないときの控え
  var DEFAULT_HOURS = {
    0: null,
    1: '5:15–15:00',
    2: '6:00–15:00',
    3: '6:00–15:00',
    4: null,
    5: '5:15–15:00',
    6: '6:00–15:00'
  };
  var DOW_JA = ['日', '月', '火', '水', '木', '金', '土'];

  /* ============================================================
     状態
     ============================================================ */
  var state = {
    root: null,
    hours: DEFAULT_HOURS,
    icsUrl: '',
    viewYear: null,
    viewMonth: null,
    events: null,      // ical.js の Event[]（読み込めたら）
    icsLoaded: false,
    monthMaps: {}      // 'YYYY-M' → Map(isoDate → { summary, kind, rank, special })
  };

  /* ============================================================
     ユーティリティ
     ============================================================ */
  function isoDate(d) {
    var y = d.getFullYear();
    var m = String(d.getMonth() + 1).padStart(2, '0');
    var day = String(d.getDate()).padStart(2, '0');
    return y + '-' + m + '-' + day;
  }

  function span(className, text) {
    var el = document.createElement('span');
    if (className) el.className = className;
    if (text != null) el.textContent = text;
    return el;
  }

  var JAPAN_TIME = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hourCycle: 'h23'
  });

  // ある時点の日本時間の年・月・日・時・分・秒
  function japanParts(date) {
    var parts = JAPAN_TIME.formatToParts(date);
    var v = {};
    for (var i = 0; i < parts.length; i++) v[parts[i].type] = Number(parts[i].value);
    v.hour = v.hour % 24;
    return v;
  }

  // 日本時間の今日（年・月・日）と、0 時からの分
  function nowInJapan() {
    var v = japanParts(new Date());
    return {
      date: new Date(v.year, v.month - 1, v.day),
      minutes: v.hour * 60 + v.minute
    };
  }

  // 予定の日時（ICAL.Time）を、日本時間の日付・時刻のまま Date にする（見ている人が海外にいても日付がずれない）
  function shopTime(t) {
    if (t.isDate) return new Date(t.year, t.month - 1, t.day);
    var v = japanParts(t.toJSDate());
    return new Date(v.year, v.month - 1, v.day, v.hour, v.minute, v.second);
  }

  function isSameDay(a, b) {
    return a.getFullYear() === b.getFullYear() &&
           a.getMonth() === b.getMonth() &&
           a.getDate() === b.getDate();
  }

  // '5:15–15:00' → { open: 315, close: 900 }
  function parseRange(hours) {
    var m = /(\d{1,2}):(\d{2})\D+(\d{1,2}):(\d{2})/.exec(hours || '');
    if (!m) return null;
    return { open: Number(m[1]) * 60 + Number(m[2]), close: Number(m[3]) * 60 + Number(m[4]) };
  }

  function dateLabel(d) {
    return (d.getMonth() + 1) + '/' + d.getDate() + '（' + DOW_JA[d.getDay()] + '）';
  }

  /* ============================================================
     ICS 取得 → ical.js Event[] 抽出
     ============================================================ */
  function fetchIcs(url) {
    if (typeof ICAL === 'undefined') {
      return Promise.reject(new Error('ical.js が読み込まれていません'));
    }
    return fetch(url).then(function (res) {
      if (!res.ok) throw new Error('HTTP ' + res.status);
      return res.text();
    }).then(function (text) {
      var comp = new ICAL.Component(ICAL.parse(text));
      return comp.getAllSubcomponents('vevent').map(function (v) { return new ICAL.Event(v); });
    });
  }

  /* ============================================================
     予定の名前から、休業か短縮営業かを決める
     - 「午前中のみ営業」「12時まで営業」「午後休業」… 短縮営業（開いている）
     - 「休業日」「定休日」「冬季休業日」「臨時休業」… 休業
     - それ以外も、このカレンダーは休業日用なので休業として扱う
     ============================================================ */
  function classify(summary) {
    var s = summary.replace(/\s+/g, '');
    if (/午後休/.test(s)) return 'partial';
    if (/休/.test(s)) return 'closed';
    if (/営業/.test(s)) return 'partial';
    return 'closed';
  }

  /* ============================================================
     月ごとの休業日マップ（ICS を読み込めたときだけ使う）
     - 終日 / 時刻あり / 複数日 / 繰り返し（RRULE・EXDATE）すべて対応
     - 1 日でも範囲がかかっていればその日に載せる
     - 同じ日に予定が重なったら、1 回きりの予定を繰り返しの予定（毎週の定休日）より優先し、
       同じ種類どうしなら休業を短縮営業より優先する（例: 木曜でも「冬季休業日」と出す、定休日の木曜に入れた「午前中のみ営業」は営業として出す）
     ============================================================ */
  function monthMap(year, month) {
    var key = year + '-' + month;
    if (!state.monthMaps[key]) state.monthMaps[key] = buildMonthMap(state.events, year, month);
    return state.monthMaps[key];
  }

  function buildMonthMap(events, year, month) {
    var monthStart = new Date(year, month, 1);
    var monthEnd = new Date(year, month + 1, 0, 23, 59, 59, 999);
    // 繰り返しの展開を打ち切る点。見ている人の時差の分ずれても取りこぼさないよう 1 日余分にとる（月の外は markRange で落とす）
    var rangeIcalEnd = ICAL.Time.fromJSDate(new Date(year, month + 1, 2), false);
    var map = new Map();

    events.forEach(function (event) {
      var summary = (event.summary || '休業').trim();
      if (event.isRecurring()) {
        var iter = event.iterator();
        var next;
        while ((next = iter.next())) {
          if (next.compare(rangeIcalEnd) >= 0) break;
          var occ = event.getOccurrenceDetails(next);
          var occEnd = shopTime(occ.endDate);
          if (occEnd <= monthStart) continue;
          markRange(map, shopTime(occ.startDate), occEnd, summary, true, monthStart, monthEnd);
        }
      } else {
        var s = shopTime(event.startDate);
        var e = event.endDate ? shopTime(event.endDate) : new Date(s.getFullYear(), s.getMonth(), s.getDate() + 1);
        if (e <= monthStart || s > monthEnd) return;
        markRange(map, s, e, summary, false, monthStart, monthEnd);
      }
    });
    return map;
  }

  function markRange(map, start, end, summary, recurring, rangeStart, rangeEnd) {
    var kind = classify(summary);
    var rank = (recurring ? 0 : 2) + (kind === 'closed' ? 1 : 0);
    var cur = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    while (cur < end) {
      if (cur >= rangeStart && cur <= rangeEnd) {
        var key = isoDate(cur);
        var prev = map.get(key);
        if (!prev || rank > prev.rank) {
          map.set(key, { summary: summary, kind: kind, rank: rank, special: !recurring });
        }
      }
      cur.setDate(cur.getDate() + 1);
    }
  }

  /* ============================================================
     ある日の営業状況 { kind: 'open' | 'partial' | 'closed', label, hours, special }
     special … 毎週の決まりでない日（ICS の 1 回きりの予定）
     ============================================================ */
  function dayStatus(date) {
    var hours = state.hours[date.getDay()];
    if (state.icsLoaded) {
      var entry = monthMap(date.getFullYear(), date.getMonth()).get(isoDate(date));
      if (entry) return { kind: entry.kind, label: entry.summary, hours: null, special: entry.special };
      return { kind: 'open', label: '営業日', hours: hours };
    }
    if (hours) return { kind: 'open', label: '営業日', hours: hours };
    return { kind: 'closed', label: '定休日', hours: null };
  }

  function nextOpenDay(from) {
    for (var i = 1; i <= 31; i++) {
      var d = new Date(from.getFullYear(), from.getMonth(), from.getDate() + i);
      var st = dayStatus(d);
      if (st.kind !== 'closed') return { date: d, status: st };
    }
    return null;
  }

  /* ============================================================
     HERO の「本日」
     文は部品の配列で組む。{ nowrap: '…' } は日付・時刻のまとまりで、途中で折り返さないよう
     <span class="kr-nowrap"> に入れる（デザインシステムの BusinessHours。文字列を流し込まず、要素を作って入れる）
     ============================================================ */
  function nw(text) { return { nowrap: text }; }

  // 「次の営業日は 10/23（金）、5:15–15:00 です。」「次の営業日は 10/27（火）、午前中のみ営業です。」
  function describeNext(next) {
    if (!next) return [];
    var head = ['次の営業日は ', nw(dateLabel(next.date))];
    if (next.status.kind === 'partial') return head.concat(['、' + next.status.label + 'です。']);
    if (next.status.hours) return head.concat(['、', nw(next.status.hours), ' です。']);
    return head.concat(['です。']);
  }

  function describeToday() {
    var now = nowInJapan();
    var today = now.date;
    var st = dayStatus(today);
    var head = ['本日 ', nw(dateLabel(today)), 'は'];

    if (st.kind === 'closed') {
      return { state: 'closed', parts: head.concat([st.label + 'です。'], describeNext(nextOpenDay(today))) };
    }
    if (st.kind === 'partial') {
      return { state: 'partial', parts: head.concat([st.label + 'です。']) };
    }
    var range = parseRange(st.hours);
    if (!range) return { state: 'open', parts: head.concat(['営業日です。']) };
    if (now.minutes < range.open) return { state: 'before', parts: head.concat([' ', nw(st.hours), ' の営業です。']) };
    if (now.minutes < range.close) return { state: 'open', parts: ['営業中です（本日 ', nw(st.hours), '）。'] };
    return { state: 'after', parts: ['本日の営業は終了しました。'].concat(describeNext(nextOpenDay(today))) };
  }

  function fillParts(el, parts) {
    var nodes = parts.map(function (p) {
      return typeof p === 'string' ? document.createTextNode(p) : span('kr-nowrap', p.nowrap);
    });
    el.replaceChildren.apply(el, nodes);
  }

  function renderToday() {
    var targets = document.querySelectorAll('[data-kr-today]');
    if (!targets.length || !state.icsLoaded) return;
    var today = describeToday();
    Array.prototype.forEach.call(targets, function (el) {
      fillParts(el, today.parts);
      el.setAttribute('data-state', today.state);
      el.hidden = false;
    });
  }

  /* ============================================================
     カレンダーの描画
     ============================================================ */
  function render() {
    var year = state.viewYear;
    var month = state.viewMonth;

    var yearEl = document.getElementById('cal-year');
    var monthNumEl = document.getElementById('cal-month-num');
    if (yearEl) yearEl.textContent = year + '年';
    if (monthNumEl) monthNumEl.textContent = (month + 1) + '月';

    var tbody = document.getElementById('cal-body');
    if (!tbody) return;
    tbody.replaceChildren();

    var gridStart = new Date(year, month, 1 - new Date(year, month, 1).getDay());
    var today = nowInJapan().date;

    for (var w = 0; w < 6; w++) {
      var tr = document.createElement('tr');
      for (var d = 0; d < 7; d++) {
        var cellDate = new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + w * 7 + d);
        tr.appendChild(buildCell(cellDate, month, today));
      }
      tbody.appendChild(tr);
    }

    var lastRow = tbody.lastElementChild;
    if (lastRow && Array.prototype.every.call(lastRow.children, function (c) {
      return c.classList.contains('is-out');
    })) {
      tbody.removeChild(lastRow);
    }

    renderNotes(year, month);
  }

  // 短縮営業と臨時の休業の日の一覧（ul.cal-notes）。表のすぐ下に置き、該当する日がない月は一覧ごと外す
  function renderNotes(year, month) {
    var grid = document.getElementById('cal-grid');
    if (!grid) return;
    var list = document.getElementById('cal-notes');
    var items = [];
    var last = new Date(year, month + 1, 0).getDate();
    for (var day = 1; day <= last; day++) {
      var date = new Date(year, month, day);
      var st = dayStatus(date);
      if (st.kind !== 'partial' && !st.special) continue;
      var li = document.createElement('li');
      var time = document.createElement('time');
      time.setAttribute('datetime', isoDate(date));
      time.textContent = dateLabel(date);
      li.appendChild(time);
      li.appendChild(document.createTextNode(st.label));
      items.push(li);
    }
    if (!items.length) {
      if (list) list.remove();
      return;
    }
    if (!list) {
      list = document.createElement('ul');
      list.id = 'cal-notes';
      list.className = 'cal-notes';
      list.setAttribute('aria-label', '短縮営業・臨時休業の日');
      grid.insertAdjacentElement('afterend', list);
    }
    list.replaceChildren.apply(list, items);
  }

  function buildCell(date, viewMonth, today) {
    var td = document.createElement('td');
    var dow = date.getDay();
    var isOut = date.getMonth() !== viewMonth;
    var isToday = isSameDay(date, today);

    if (isOut) td.classList.add('is-out');
    if (dow === 0) td.classList.add('is-sun');
    if (dow === 6) td.classList.add('is-sat');
    if (isToday && !isOut) td.classList.add('is-today');

    td.appendChild(span('day', String(date.getDate())));
    if (isOut) return td;

    if (isToday) td.appendChild(span('day-tag', '本日'));

    var st = dayStatus(date);
    td.classList.add('is-' + st.kind);
    if (st.special) td.classList.add('is-special');
    td.appendChild(span('status', st.label));
    if (st.hours) {
      var parts = st.hours.split('–');
      var hoursEl = span('hours');
      hoursEl.appendChild(span('h-open', parts[0] + '–'));
      if (parts[1]) hoursEl.appendChild(span('h-close', parts[1]));
      td.appendChild(hoursEl);
    }
    return td;
  }

  /* ============================================================
     月切替
     ============================================================ */
  function shiftMonth(delta) {
    var d = new Date(state.viewYear, state.viewMonth + delta, 1);
    state.viewYear = d.getFullYear();
    state.viewMonth = d.getMonth();
    render();
  }

  function bindNav() {
    var prev = document.getElementById('cal-prev');
    var next = document.getElementById('cal-next');
    if (prev) prev.addEventListener('click', function () { shiftMonth(-1); });
    if (next) next.addEventListener('click', function () { shiftMonth(1); });
  }

  function setLoading(visible) {
    var el = document.getElementById('cal-loading');
    if (!el) return;
    if (visible) el.removeAttribute('hidden');
    else el.setAttribute('hidden', '');
  }

  function setFootNote(message) {
    var el = document.getElementById('cal-foot-note');
    if (el) el.textContent = message;
  }

  /* ============================================================
     起動
     ============================================================ */
  function readConfig(root) {
    try {
      var parsed = JSON.parse(root.getAttribute('data-hours') || '');
      if (parsed && typeof parsed === 'object') state.hours = parsed;
    } catch (e) {
      console.warn('Kobe Ribbon Calendar: data-hours を読めないので既定の営業時間を使います', e);
    }
    state.icsUrl = root.getAttribute('data-ics-url') || '';
  }

  function init() {
    // section が無いページ（他テンプレートで読まれた場合）では何もしない
    var root = document.getElementById('calendar');
    if (!root || !document.getElementById('cal-body')) return;
    state.root = root;
    readConfig(root);

    var today = nowInJapan().date;
    state.viewYear = today.getFullYear();
    state.viewMonth = today.getMonth();
    bindNav();
    render();

    if (!state.icsUrl) return;
    setLoading(true);
    fetchIcs(state.icsUrl)
      .then(function (events) {
        state.events = events;
        state.monthMaps = {};
        state.icsLoaded = true;
        render();
        renderToday();
      })
      .catch(function (err) {
        console.error('Kobe Ribbon Calendar:', err);
        setFootNote('※ 休業日を読み込めませんでした。曜日ごとの定休日だけを表示しています。');
      })
      .finally(function () {
        setLoading(false);
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
